import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout } from "@pimo/pimo-components/src/lib/layouts/drag-and-drop-layout/drag-and-drop-layout";
import {
  PMCountCategoriesCard,
  PMDonutChartCard,
  PMSmallNumber,
  PMSmallNumberCardEditSchema,
} from "@pimo/pimo-components/src/lib/pm-components";

import { TestAppState } from "../../../app";
import { getDropdownCount } from "../../../utils/dropdown-options";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import { getOverviewCardCopy } from "../get-card-copy";

export function addItemCompletionCountsAndSemaphoreStatusCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
): void {
  const { language, projectName, projectNamePlural, getCardCopy } =
    getOverviewCardCopy(app);

  const ratioOfCompletedItemsComponent = view.addComponent({
    component: PMSmallNumber,
  });

  ratioOfCompletedItemsComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "ratioOfCompletedItemsComponent",
      labels: ["#ofItems"],
      description: getCardCopy(language, "numberOfCompletedItems").description,
      title: getCardCopy(language, "itemCompletionCounts").title,
      editSchema: PMSmallNumberCardEditSchema,
    };
  });

  ratioOfCompletedItemsComponent.mapState(({ dashboard, ...appState }) => {
    return {
      showInfoIcon: false,
      showMenuIcon: false,
      title: getLocalizedCardCopy(
        appState.language || "en",
        "itemCompletionCounts",
        {
          itemName: getItemName(
            appState.settings?.configuration?.itemName,
            appState.language || "en"
          ),
          itemNamePlural: getPluralOfItemName(
            appState.settings?.configuration?.itemName,
            appState.language || "en"
          ),
          projectName,
          projectNamePlural,
        }
      ).title,
      number: `${dashboard?._calculated?.numberOfClosedSubEntities ?? 0}/${
        dashboard?.projects?.flatMap((project) => project.reportingItems ?? [])
          .length ?? 0
      }`,

      description: getLocalizedCardCopy(
        appState.language || "en",
        "numberOfCompletedItems",
        {
          itemName: getItemName(
            appState.settings?.configuration?.itemName,
            appState.language || "en"
          ),
          itemNamePlural: getPluralOfItemName(
            appState.settings?.configuration?.itemName,
            appState.language || "en"
          ),
          projectName,
          projectNamePlural,
        }
      ).description,
    };
  });

  const countCompletionOfItemsComponent = view.addComponent({
    component: PMCountCategoriesCard,
    layoutProps: {
      componentId: "countCompletionOfItemsComponent",
      labels: ["completion", "count"],
      description: getCardCopy(language, "itemCompletionCounts").description,
      title: getCardCopy(language, "itemCompletionCounts").title,
    },
  });

  countCompletionOfItemsComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "countCompletionOfItemsComponent",
      labels: ["completion", "count"],
      title: getCardCopy(language, "itemCompletionCounts").title,
      description: getCardCopy(language, "itemCompletionCounts").description,
    };
  });

  countCompletionOfItemsComponent.mapState(
    ({ dashboard, settings, language }) => {
      const degreeOfCompletionCounts =
        dashboard?._calculated?.degreeOfCompletionCounts ?? {};

      const data = (
        settings?.configuration?.degreeOfCompletionDefinition ?? []
      ).map((entry) => {
        return {
          label: entry.name ?? "",
          count: getDropdownCount(degreeOfCompletionCounts, entry),
          color: entry.color ?? "",
        };
      });

      return {
        title: getLocalizedCardCopy(language, "itemCompletionCounts", {
          itemName: getItemName(settings?.configuration?.itemName, language),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            language
          ),
          projectName,
          projectNamePlural,
        }).title,
        icon: "DonutLarge",
        data,
      };
    }
  );

  const semaphoreStatusOfItemsDonutChartComponent = view.addComponent({
    component: PMDonutChartCard,
    layoutProps: {
      componentId: "statusOfItemsDonutChartComponent",
      title: "",
      description: "",
      labels: ["donutChart", "chart", "status"],
    },
  });
  semaphoreStatusOfItemsDonutChartComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "statusOfItemsDonutChartComponent",
      labels: ["donutChart", "chart", "semaphoreStatus"],
      title: getCardCopy(language, "statusOfItems").title,
      description: getCardCopy(language, "statusOfItems").description,
    };
  });

  semaphoreStatusOfItemsDonutChartComponent.mapState(
    ({ dashboard, settings, language }) => {
      const statusCounts =
        dashboard?._calculated?.itemSemaphoreStatusCounts ?? {};
      const data =
        [
          settings?.configuration?.semaphoreStatusDefinition?.[0].low,
          settings?.configuration?.semaphoreStatusDefinition?.[0].medium,
          settings?.configuration?.semaphoreStatusDefinition?.[0].high,
        ]?.map((entry) => {
          return {
            label: entry?.name ?? "",
            value: (entry && getDropdownCount(statusCounts, entry)) ?? 0,
            color: entry?.color ?? "",
          };
        }) ?? [];

      return {
        title: getLocalizedCardCopy(language, "statusOfItems", {
          itemName: getItemName(settings?.configuration?.itemName, language),
          itemNamePlural: getPluralOfItemName(
            settings?.configuration?.itemName,
            language
          ),
          projectName,
          projectNamePlural,
        }).title,
        icon: "DonutLarge",
        data,
      };
    }
  );

  semaphoreStatusOfItemsDonutChartComponent.mapVisibility(({ settings }) => {
    return (
      (settings?.configuration?.statusReportingLevel ?? "overall") ===
      "category"
    );
  });
}
